// Note: design spec calls for text color #FBF7F3 (Primary-1), but it doesn't
// exist in the palette. Using `linen` (#F9F5F0) as the closest available token.
import { FaFacebookF, FaInstagram, FaLinkedin } from 'react-icons/fa'

import { useAuth } from 'src/providers/AuthProvider'
import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { useUser } from 'src/providers/UserProvider'

import { Image } from 'src/components/atoms/Image'
import {
  ExternalLink,
  InternalLink,
  OnClickLink
} from 'src/components/atoms/Link'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import {
  aboutUsPath,
  becomeAPlanterPath,
  blogPath,
  businessPackagesPath,
  businessTakeActionPath,
  communityPath,
  contactPath,
  dashboardPath,
  donatePath,
  ourBusinessFundersPath,
  giftingPath,
  giftingBusinessPath,
  tourismIntroPath,
  homePath,
  impactPath,
  individualsFamiliesTakeActionPath,
  listPlantingProjectsPath,
  listRegistriesPath,
  privacyPolicyPath,
  termsPath
} from 'src/config/paths'

import { LinkGroup } from './types'

export const socialLinks = [
  {
    IconComponent: FaFacebookF,
    href: 'https://www.facebook.com/TreesThatCount',
    label: 'Facebook'
  },
  {
    IconComponent: FaInstagram,
    href: 'https://www.instagram.com/treesthatcount',
    label: 'Instagram'
  },
  {
    IconComponent: FaLinkedin,
    href: 'https://www.linkedin.com/company/treesthatcount',
    label: 'LinkedIn'
  }
]

const footerLinks: LinkGroup[] = [
  {
    header: 'For individuals',
    headerPath: individualsFamiliesTakeActionPath,
    links: [
      { label: 'Overview', path: individualsFamiliesTakeActionPath },
      { label: 'Gift a tree', path: giftingPath },
      { label: 'Registries', path: listRegistriesPath }
    ]
  },
  {
    header: 'For businesses',
    headerPath: businessTakeActionPath,
    links: [
      { label: 'Overview', path: businessTakeActionPath },
      { label: 'Business packages', path: businessPackagesPath() },
      { label: 'Corporate gifting', path: giftingBusinessPath },
      { label: 'For tourism businesses', path: tourismIntroPath },
      { label: 'Registries', path: listRegistriesPath },
      { label: 'Our business funders', path: ourBusinessFundersPath() }
    ]
  },
  {
    header: 'Impact',
    headerPath: impactPath,
    links: [
      { label: 'Our impact', path: impactPath },
      { label: 'Projects', path: listPlantingProjectsPath() },
      { label: 'News and stories', path: blogPath() }
    ]
  },
  {
    header: 'About',
    headerPath: aboutUsPath,
    links: [
      { label: 'About us', path: aboutUsPath },
      { label: 'Our community', path: communityPath },
      { label: 'Become a planter', path: becomeAPlanterPath },
      { label: 'Contact us', path: contactPath }
    ]
  }
]

const getLinkId = (groupHeader: string, linkLabel: string) =>
  `link-${groupHeader}-${linkLabel}`
    .replace(/[^a-z0-9]/gi, '-')
    .toLocaleLowerCase()

export const FooterRebrand = () => {
  const { isFocus } = useFocusActionState()
  const { user } = useUser()
  const { loginWithCustomRedirect } = useAuth()

  if (isFocus) return null

  return (
    <footer className='w-full bg-wineRich text-linen'>
      <div className='container mx-auto px-4 lg:px-20 pt-8 pb-12 lg:pt-12 lg:pb-16'>
        <div className='flex max-w-[1040px] flex-col gap-8 lg:gap-10'>
          <InternalLink
            href={homePath}
            className='relative my-6 block h-[46px] w-[189px] max-w-full shrink-0 lg:my-10 lg:h-[81px] lg:w-[336px]'
          >
            <Image
              fill
              alt='Trees That Count'
              src='/img/logos/ttc-logo-rebrand.svg'
              className='object-contain object-left'
            />
          </InternalLink>

          <nav
            aria-label='Footer'
            className='grid w-full grid-cols-2 gap-8 lg:grid-cols-5'
          >
            {footerLinks.map(linkGroup => (
              <div
                key={linkGroup.header}
                className='flex min-w-[130px] flex-col gap-4 lg:min-w-[160px] lg:gap-6'
              >
                {linkGroup.headerPath ? (
                  <InternalLink
                    href={linkGroup.headerPath}
                    color='none'
                    className='text-linen! hover:underline'
                  >
                    <TypographyRebrand variant='eyebrow'>
                      {linkGroup.header}
                    </TypographyRebrand>
                  </InternalLink>
                ) : (
                  <TypographyRebrand variant='eyebrow'>
                    {linkGroup.header}
                  </TypographyRebrand>
                )}
                <ul className='flex flex-col gap-2'>
                  {linkGroup.links.map(link => (
                    <li
                      key={link.label}
                      id={getLinkId(linkGroup.header, link.label)}
                      className='text-sm leading-[1.6] opacity-80 transition-opacity hover:opacity-100 lg:text-base'
                    >
                      {link.isExternal ? (
                        <ExternalLink
                          className='text-linen! hover:underline'
                          href={link.path}
                        >
                          {link.label}
                        </ExternalLink>
                      ) : (
                        <InternalLink
                          className='text-linen! hover:underline'
                          href={link.path}
                        >
                          {link.label}
                        </InternalLink>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className='flex min-w-[130px] flex-col gap-4 lg:min-w-[160px] lg:gap-6'>
              <span className='font-medium'>
                <TypographyRebrand variant='eyebrow'>
                  Get involved
                </TypographyRebrand>
              </span>
              <ul className='flex flex-col gap-2'>
                <li className='text-sm leading-[1.6] opacity-80 transition-opacity hover:opacity-100 lg:text-base'>
                  <InternalLink
                    className='text-linen! hover:underline'
                    href={donatePath()}
                  >
                    Donate
                  </InternalLink>
                </li>
                {!user && (
                  <li className='text-sm leading-[1.6] opacity-80 transition-opacity hover:opacity-100 lg:text-base'>
                    <OnClickLink
                      className='text-linen! font-normal underline hover:no-underline'
                      onClick={() => {
                        loginWithCustomRedirect(dashboardPath())
                      }}
                    >
                      Log in
                    </OnClickLink>
                  </li>
                )}
              </ul>
            </div>
          </nav>
        </div>

        <div className='mt-10 flex flex-col items-center gap-6 border-t border-linen/10 pt-8 lg:mt-16 lg:flex-row lg:items-end lg:justify-between lg:border-t-0 lg:pt-0'>
          <div className='flex flex-col items-center gap-6 lg:items-start'>
            <ul className='flex gap-4 lg:gap-3'>
              {socialLinks.map(socialLink => (
                <li key={socialLink.href}>
                  <ExternalLink
                    href={socialLink.href}
                    aria-label={socialLink.label}
                    className='flex h-12 w-12 items-center justify-center rounded-full border border-linen/40 text-linen! transition-colors hover:bg-linen/10'
                  >
                    <socialLink.IconComponent size='20' />
                  </ExternalLink>
                </li>
              ))}
            </ul>
            <div className='flex gap-3 text-sm leading-[1.6] opacity-70 lg:text-base'>
              <InternalLink
                className='text-linen! hover:underline'
                href={privacyPolicyPath}
              >
                Privacy policy
              </InternalLink>
              <span aria-hidden>|</span>
              <InternalLink
                className='text-linen! hover:underline'
                href={termsPath}
              >
                Terms of use
              </InternalLink>
            </div>
          </div>

          <span className='text-sm leading-[1.6] opacity-70 lg:text-base'>
            &copy; {new Date().getFullYear()} Trees That Count, New Zealand
          </span>
        </div>
      </div>
    </footer>
  )
}
